/**
 * Seed data for the `fx-currency-expenses` prototype.
 *
 * A label's books are in one currency but its invoices are not, so the list
 * mixes both: most expenses in the label currency (USD) and a handful in CAD,
 * EUR and GBP, each booked at the rate for its own date. That mix is what makes
 * the Value column worth reading — the converted rows are the ones carrying an
 * info icon.
 */
import type { Expense } from "@/types/expenses";
import type { Release } from "@/types/releases";
import type { Recording } from "@/types/recordings";
import {
  AMOUNT_DECIMALS,
  LABEL_CURRENCY,
  RATE_DECIMALS,
  convertToLabelCurrency,
} from "./exchangeRates";

export const LABEL_ID = "dev-label";

// --- Catalogue -------------------------------------------------------------
// Only what the Add / Edit expense flow reaches for: the release and track
// comboboxes, and the "Applied to" column on the list.

interface SeedRelease {
  id: string;
  title: string;
  type: Release["type"];
  catalog_number: string;
  release_date: string;
  tracks: { id: string; title: string; isrc: string }[];
}

const SEED_RELEASES: SeedRelease[] = [
  {
    id: "rel-neon-arcadia",
    title: "Neon Arcadia",
    type: "album",
    catalog_number: "NEO-001",
    release_date: "2025-03-14",
    tracks: [
      { id: "trk-arcadia-gates", title: "Arcadia Gates", isrc: "USNEO2500001" },
      { id: "trk-midnight-transit", title: "Midnight Transit", isrc: "USNEO2500002" },
      { id: "trk-hologram-city", title: "Hologram City", isrc: "USNEO2500003" },
      { id: "trk-afterglow", title: "Afterglow", isrc: "USNEO2500004" },
    ],
  },
  {
    id: "rel-half-light",
    title: "Half Light",
    type: "ep",
    catalog_number: "HLF-014",
    release_date: "2025-06-27",
    tracks: [
      { id: "trk-half-light", title: "Half Light", isrc: "USHLF2500001" },
      { id: "trk-slow-exit", title: "Slow Exit", isrc: "USHLF2500002" },
      { id: "trk-paper-lantern", title: "Paper Lantern", isrc: "USHLF2500003" },
    ],
  },
  {
    id: "rel-static-bloom",
    title: "Static Bloom",
    type: "single",
    catalog_number: "STB-003",
    release_date: "2026-01-30",
    tracks: [
      { id: "trk-static-bloom", title: "Static Bloom", isrc: "USSTB2600001" },
      { id: "trk-static-bloom-edit", title: "Static Bloom (Radio Edit)", isrc: "USSTB2600002" },
    ],
  },
  {
    id: "rel-paper-cities",
    title: "Paper Cities",
    type: "album",
    catalog_number: "PPC-021",
    release_date: "2024-11-08",
    tracks: [
      { id: "trk-paper-cities", title: "Paper Cities", isrc: "USPPC2400001" },
      { id: "trk-north-window", title: "North Window", isrc: "USPPC2400002" },
      { id: "trk-carrier", title: "Carrier", isrc: "USPPC2400003" },
    ],
  },
  {
    id: "rel-undertow",
    title: "Undertow",
    type: "ep",
    catalog_number: "UND-007",
    release_date: "2026-04-17",
    tracks: [
      { id: "trk-undertow", title: "Undertow", isrc: "USUND2600001" },
      { id: "trk-low-tide", title: "Low Tide", isrc: "USUND2600002" },
    ],
  },
];

const NOW = "2026-09-01T09:00:00Z";

export const RELEASES: Release[] = SEED_RELEASES.map((release) => ({
  id: release.id,
  title: release.title,
  type: release.type,
  catalog_number: release.catalog_number,
  release_date: release.release_date,
  label: LABEL_ID,
  created_at: NOW,
  updated_at: NOW,
  track_count: release.tracks.length,
  status: "released",
  can_delete: true,
}));

export const RECORDINGS: Recording[] = SEED_RELEASES.flatMap((release) =>
  release.tracks.map((track, index) => ({
    id: track.id,
    title: track.title,
    isrc: track.isrc,
    track_number: String(index + 1),
    contributors: [],
    can_delete: true,
    releases: RELEASES.filter((r) => r.id === release.id),
    created_at: NOW,
    updated_at: NOW,
  })),
);

export function recordingsForRelease(releaseId: string): Recording[] {
  return RECORDINGS.filter((recording) => recording.releases.some((r) => r.id === releaseId));
}

// --- Expenses --------------------------------------------------------------

interface SeedExpense {
  id: string;
  name: string;
  date: string;
  expense_type: Expense["expense_type"];
  total_amount: string;
  /** The currency on the invoice. Defaults to the label currency. */
  currency?: string;
  /** A release id, a track id, or nothing — the three "Applied to" states. */
  appliedTo?: string;
  recoupable?: boolean;
  /** Rows with an invoice attached: paperclip in the table, Download in the menu. */
  hasFile?: boolean;
  /** Already picked up by a royalty statement, so the app refuses to delete it. */
  locked?: boolean;
}

const SEED_EXPENSES: SeedExpense[] = [
  {
    id: "exp-001",
    name: "Neon Arcadia — mastering",
    date: "2026-08-24",
    expense_type: "mastering",
    total_amount: "2400.00",
    appliedTo: "rel-neon-arcadia",
    hasFile: true,
  },
  {
    id: "exp-002",
    name: "Sterling Sound mix revisions",
    date: "2026-08-19",
    expense_type: "mixing",
    total_amount: "1850.50",
    appliedTo: "trk-midnight-transit",
    hasFile: true,
  },
  {
    id: "exp-015",
    name: "Toronto session musicians",
    date: "2026-08-15",
    expense_type: "recording",
    total_amount: "120.00",
    currency: "CAD",
    appliedTo: "trk-hologram-city",
  },
  {
    id: "exp-003",
    name: "Cover artwork — Static Bloom",
    date: "2026-08-11",
    expense_type: "artwork",
    total_amount: "950.00",
    appliedTo: "rel-static-bloom",
  },
  {
    id: "exp-016",
    name: "Berlin mastering studio",
    date: "2026-08-07",
    expense_type: "mastering",
    total_amount: "2450.00",
    currency: "EUR",
    appliedTo: "rel-half-light",
    hasFile: true,
  },
  {
    id: "exp-004",
    name: "Playlist pitching retainer",
    date: "2026-08-04",
    expense_type: "promotion",
    total_amount: "3200.00",
    locked: true,
    hasFile: true,
  },
  {
    id: "exp-005",
    name: "Vinyl test pressings",
    date: "2026-07-29",
    expense_type: "vinyl_manufacturing",
    total_amount: "5400.00",
    appliedTo: "rel-paper-cities",
    hasFile: true,
  },
  {
    id: "exp-006",
    name: "Session drums — Afterglow",
    date: "2026-07-22",
    expense_type: "recording",
    total_amount: "780.00",
    appliedTo: "trk-afterglow",
  },
  {
    id: "exp-007",
    name: "Remix fee — Low Tide",
    date: "2026-07-15",
    expense_type: "remix_fees",
    total_amount: "1500.00",
    appliedTo: "trk-low-tide",
    hasFile: true,
  },
  {
    id: "exp-017",
    name: "London string session",
    date: "2026-07-09",
    expense_type: "recording",
    total_amount: "1800.00",
    currency: "GBP",
    appliedTo: "trk-paper-lantern",
    hasFile: true,
  },
  {
    id: "exp-008",
    name: "Video edit — Half Light",
    date: "2026-07-03",
    expense_type: "video_editing",
    total_amount: "2260.75",
    appliedTo: "rel-half-light",
  },
  {
    id: "exp-009",
    name: "CD run (1000 units)",
    date: "2026-06-26",
    expense_type: "cd_manufacturing",
    total_amount: "1740.00",
    appliedTo: "rel-paper-cities",
    hasFile: true,
  },
  {
    id: "exp-010",
    name: "Anti-piracy monitoring — Q2",
    date: "2026-06-18",
    expense_type: "anti_piracy_prevention",
    total_amount: "640.00",
    recoupable: false,
  },
  {
    id: "exp-018",
    name: "EU press campaign",
    date: "2026-06-12",
    expense_type: "promotion",
    total_amount: "640.00",
    currency: "EUR",
  },
  {
    id: "exp-011",
    name: "In-house studio time",
    date: "2026-06-09",
    expense_type: "in_house_costs",
    total_amount: "1120.00",
    appliedTo: "rel-undertow",
    recoupable: false,
  },
  {
    id: "exp-012",
    name: "Mastering — Carrier",
    date: "2026-05-28",
    expense_type: "mastering",
    total_amount: "420.00",
    appliedTo: "trk-carrier",
  },
  {
    id: "exp-013",
    name: "Press photos and retouching",
    date: "2026-05-14",
    expense_type: "other",
    total_amount: "890.00",
    appliedTo: "rel-neon-arcadia",
    hasFile: true,
  },
  {
    id: "exp-014",
    name: "Radio promo campaign",
    date: "2026-04-30",
    expense_type: "promotion",
    total_amount: "4100.00",
    appliedTo: "rel-half-light",
    locked: true,
  },
];

function releaseDetails(id: string) {
  const release = RELEASES.find((r) => r.id === id);
  return release ? { id: release.id, title: release.title, artwork: null } : null;
}

function recordingDetails(id: string) {
  const recording = RECORDINGS.find((r) => r.id === id);
  if (!recording) return null;
  return {
    id: recording.id,
    title: recording.title,
    release_id: recording.releases[0]?.id ?? null,
  };
}

/** The list as it looks on a fresh load — rebuilt on every Reset. */
export function seedExpenses(invoiceUrl: string): Expense[] {
  return SEED_EXPENSES.map((seed) => {
    const release = seed.appliedTo ? releaseDetails(seed.appliedTo) : null;
    const recording = seed.appliedTo && !release ? recordingDetails(seed.appliedTo) : null;
    const currency = seed.currency ?? LABEL_CURRENCY;
    // Booked at the rate for the expense's own date, the same way the mock
    // books one the user enters.
    const conversion = convertToLabelCurrency(Number(seed.total_amount), currency, seed.date);
    return {
      id: seed.id,
      name: seed.name,
      date: seed.date,
      expense_type: seed.expense_type,
      total_amount: seed.total_amount,
      currency,
      exchange_rate: conversion ? conversion.rate.toFixed(RATE_DECIMALS) : null,
      converted_amount: conversion ? conversion.converted.toFixed(AMOUNT_DECIMALS) : null,
      release: release?.id,
      sound_recording: recording?.id,
      release_details: release,
      sound_recording_details: recording,
      recoupable: seed.recoupable ?? true,
      can_delete: !seed.locked,
      file: seed.hasFile ? invoiceUrl : null,
      extraction_status: null,
      created_at: `${seed.date}T10:00:00Z`,
      updated_at: `${seed.date}T10:00:00Z`,
    } satisfies Expense;
  });
}
